import React from 'react';
import { Player } from "@remotion/player"
import { MyComp } from "./remotion/MyComp"
import getImageSize from 'image-size-from-url';
import translations from './lang/App.lang.json';
import I18N from './classes/I18N';
import ListingSelector from './components/ListingSelector'
import BrandingSelector from './components/BrandingSelector'
import GettingStartedPanel from './components/GettingStartedPanel'
import MusicSelector from './components/MusicSelector'
import ExportVideoModal from './components/ExportVideoModal';
import ImageSelector from './components/ImageSelector';
import ProgressBar from './components/base/ProgressBar';
import BaseConfirm from './components/base/BaseConfirm';
import './App.css';
import {setupWhiteLabel} from "./lib/whitelabel/WhiteLabel";
import styles from "./components/MusicSelector.module.css";

const url = new URL(window.location.href);
const jwt = url.searchParams.get('token');
const editId = url.searchParams.get('edit_id');
const assetId = url.searchParams.get('asset_id');
const templateId = url.searchParams.get('template_id');
const isUpdate = url.searchParams.get('is_update');
let mlsId = url.searchParams.get('mls_id');
let listingId = url.searchParams.get('listing_id');
let isSold = url.searchParams.get('is_sold');

const i18n = new I18N(translations);

class App extends React.Component {
  constructor() {
    super();
    this.state = {
      listingData: null,
      user: null,
      account: null,
      selectedAgent: null,
      selectedCoAgent: null,
      selectedTeam: null,
      selectedBrokerage: null,
      selectedBrand: null,
      selectedSong: null,
      musicEnabled: null,
      voiceoverEnabled: null,
      voiceoverS3Url: null,
      preloadedVoiceoverFile: null,
      selectedImages: null,
      selectedScope: {
        label: "Agent",
        selected: true,
        value: "agent"
      },
      showExportModal: false,
      settings: null,
      selectedVoiceoverFile: null,
      mode: (isUpdate && assetId) ? 'edit' : 'create',
      preloadedDataReady: false,
      imagesWereReset: false,
      loading: false,
      rendering: false,
      disclaimerActive: false,
      disabledBranding: [],
      processingImages: false,
      currentImage: 0,
      totalImages: 0,
      imagesReady: false,
      showResetPhotosConfirmation: false,
      locale: 'en',
      canRenderApp: false,
      uploadingVoiceover: false,
      loadingListing: false
    };
    this.brandingSelector = React.createRef();
    this.imageSelector = React.createRef();
    this.setCurrentUser();
    this.player = React.createRef();

    if (mlsId && listingId) {
      this.loadListingData();
    }
  }

  setValue = (listing) => {
    this.setState({imagesReady: false, listingData: listing})
  };

  setImages = async (images) => {
    const processed = await this.processImages(images);
    this.setState({selectedImages: processed, imagesReady: true, processingImages: false});
    const img = images.find(img => img.selected === true);
    if (img && img.frameStart !== null && img.frameStart !== undefined && this.player?.current) {
      this.player.current.seekTo(img.frameStart + 20); // need to offset by 20 to factor in the animation.
    }
  }

  processImages = async (images) => {
    let currentImage = 0;
    this.setState({processingImages: true, currentImage, totalImages: images.length});
    let processed = [];
    // eslint-disable-next-line
    for (const image of images) {
      this.setState({currentImage: currentImage++});
      const {width, height} = await getImageSize(image.url);

      processed.push({
        ...image,
        width,
        height
      });
    }

    return processed;
  }

  confirmResetImages = () => {
    this.setState({ showResetPhotosConfirmation: true });
  }

  confirmedResetImages = async () => {
    this.setState({ listingData: null, showResetPhotosConfirmation: false, imagesWereReset: true });

    fetch(`${process.env.REACT_APP_KVCORE_API_URI}/listings/${this.state.listingData.mls}/${this.state.listingData.mlsid}?forDisplay=1&useElastic=1`, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${jwt}`
      }
    }).then(response => response.json()).then(({data}) => {
      this.setValue(data);
    });
  }

  cancelledResetImages = () => {
    this.setState({ showResetPhotosConfirmation: false });
  }

  setCurrentUser = () => {
    fetch(`${process.env.REACT_APP_KVCORE_API_URI}/v2/internal/auth/user?include_admin_info=1&bust=${Date.now()}`, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${jwt}`
      }
    }).then(res => res.json()).then(data => {
      const user = data.user;
      this.setState({ user });
      this.setState({ account: data.account.account_name });
      setupWhiteLabel(data.account.whitelabel);

      // Set user's language preference
      if (user?.user_settings && user.user_settings.length) {
        const langSetting = user.user_settings.find(({setting}) => {
          return setting === 'language_preference';
        });

        if (langSetting) {
          this.setState({ locale: langSetting.value });
        }
      }

      if (!user.company_admin && !user.adminInfo.isTeamAdmin && !user.adminInfo.isTeamMember) {
        this.setState({ disabledBranding: ['team'] });
      } else {
        this.setState({ disabledBranding: [] });
      }

      if (isUpdate && assetId) {
        this.loadInputProps();
      }

      this.setState({ canRenderApp: true });

      this.loadMarketingSettings();
    });
  }

  loadMarketingSettings = (queryParams = {}) => {
    const query = {
      user_id: this.state.user?.id,
      ...queryParams
    };

    fetch(`${process.env.REACT_APP_KVCORE_API_URI}/v2/internal/clm/marketing/settings/getUserSettings?${(new URLSearchParams(query)).toString()}`, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${jwt}`
      }
    }).then(res => res.json()).then(data => {
      this.setState({settings: data})

      const disclaimerOnAllAssets = data.find((data) => {
        return data.setting === 'disclaimer_include_templates' && data.value === 'true';
      });

      if(disclaimerOnAllAssets) {
        this.setState({ disclaimerActive: true })
      }
    });
  }

  loadListingData = () => {
    this.setState({loadingListing: true});

    const query = {
      forDisplay: 1,
      useElastic: 1
    };

    if (isSold) {
      query.isSold = 1;
    }

    fetch(`${process.env.REACT_APP_KVCORE_API_URI}/listings/${mlsId}/${listingId}?${(new URLSearchParams(query)).toString()}`, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${jwt}`
      }
    }).then(res => res.json()).then(({data}) => {
      this.setState({listingData: data});
    }).catch((error) => {
      console.log('error loading listing:', error);
      mlsId = listingId = null;
    }).finally(() => {
      this.setState({loadingListing: false});
    });
  }

  loadInputProps = () => {
    this.setState({ loading: true });
    const configUrl = editId
      ? `https://kunversion-accounts.s3.amazonaws.com/marketing-center/${this.state.account}/${editId}.json`
      : `https://kunversion-accounts.s3.amazonaws.com/marketing-center/${this.state.account}/video/asset_${assetId}/config.json`;

    fetch(`${configUrl}?cache_bust=${Date.now()}`, {
      method: 'GET'
    })
      .then(res => res.json())
      .then((data) => {
        // Set input props based on JSON data received from S3
        this.setState({
          listingData: data.inputProps.listing,
          selectedAgent: data.inputProps.user,
          selectedCoAgent: data.inputProps.coAgent,
          selectedTeam: data.inputProps.team,
          selectedBrokerage: data.inputProps.brokerage,
          selectedScope: data.inputProps.scope,
          selectedSong: data.inputProps.song,
          musicEnabled: data.inputProps.musicEnabled,
          selectedImages: data.inputProps.images,
          settings: data.inputProps.settings,
          locale: data.inputProps?.locale ? data.inputProps.locale : 'en',
          voiceoverEnabled: data.inputProps.voiceoverEnabled,
          preloadedVoiceoverFile: data.inputProps.voiceoverFile,
          voiceoverS3Url: data.inputProps.voiceoverFile ? data.inputProps.voiceoverFile.url : null,
          selectedBrand: data.inputProps.brand ? data.inputProps.brand : null
        });
      })
      .finally(() => {
        this.setState({ loading: false, preloadedDataReady: true });
      });
  }

  setAgent = (agent) => {
    this.setState({selectedAgent: agent});
  }

  setCoAgent = (coagent) => {
    this.setState({selectedCoAgent: coagent});
  }

  setScope = (selectedScope) => {
    this.setState({selectedScope: selectedScope});
  }

  setBrokerage = (brokerage) => {
    this.setState({selectedBrokerage: brokerage});
  }

  setBrand = (brand) => {
    this.setState({ selectedBrand: brand });
    this.loadMarketingSettings({
      'types[]': 'brand',
      'type_id': brand.id
    });
  }

  setTeam = (team) => {
    this.setState({selectedTeam: team});
  }

  setSong = (song) => {
    this.setState({selectedSong: song});
  }

  setMusicEnabled = (enabled) => {
    this.setState({musicEnabled: enabled});
  }

  setDisclaimerEnabled = () => {
    const enabled = !this.state.disclaimerActive;
    this.setState({disclaimerActive: enabled});
  }

  setVoiceoverEnabled = (enabled) => {
    this.setState({voiceoverEnabled: enabled});
  }

  clearPreloadedVoiceover = () => {
    this.setState({ preloadedVoiceoverFile: null });
  }

  setShowExportModal = (show) => {
    this.setState({showExportModal: show});
  }

  exportVideo = () => {
    this.setState({rendering: true});
    let inputProps = this.getInputProps();

    // Only post the data we need to render the video
    if (inputProps.user) {
      const agentData = {
        id: inputProps?.user?.id,
        photo: inputProps?.user?.photo,
        full_name: inputProps?.user?.full_name,
        cell_phone: inputProps?.user?.cell_phone,
        email: inputProps?.user?.email,
        title: inputProps?.user?.title,
        credentials: inputProps?.user?.credentials
      };

      inputProps.user = agentData;
    }

    if (inputProps.coAgent) {
      const coAgentData = {
        id: inputProps?.coAgent?.id,
        photo: inputProps?.coAgent?.photo,
        full_name: inputProps?.coAgent?.full_name,
        cell_phone: inputProps?.coAgent?.cell_phone,
        email: inputProps?.coAgent?.email,
        title: inputProps?.coAgent?.title,
        credentials: inputProps?.coAgent?.credentials
      };

      inputProps.coAgent = coAgentData;
    }

    if (inputProps.team) {
      const team = inputProps?.team;
      let teamWebsite = null;

      if (team && team.website_url.length) {
        teamWebsite = team.website_url;
      } else if (team && team.website?.data?.domain) {
        teamWebsite = team.website.data.domain;
      }

      const teamData = {
        id: inputProps?.team?.id,
        photo: inputProps?.team?.photo,
        name: inputProps?.team?.name,
        email: inputProps?.team?.email,
        phone: inputProps?.team?.phone,
        website: teamWebsite
      };

      inputProps.team = teamData;
    }

    if (inputProps.office) {
      const officeData = {
        name: inputProps?.office?.name,
        phone: inputProps?.office?.phone
      }

      inputProps.office = officeData;
    }

    inputProps.brokerage = {
      id: inputProps?.brokerage?.id,
      business_photo: inputProps?.brokerage?.business_photo
    };

    if (inputProps.voiceoverEnabled) {
      if (inputProps.preloadedVoiceoverFile) {
        inputProps.voiceoverFile = inputProps.preloadedVoiceoverFile;
      } else {
        inputProps.voiceoverFile = {
          ...inputProps.voiceoverFile,
          url: this.state.voiceoverS3Url
        }
      }
    }

    // Trim the listing down to only the fields we need
    if (inputProps.listing) {
      const trimmedListing = {
        mls: inputProps.listing.mls,
        mlsid: inputProps.listing.mlsid,
        address: inputProps.listing.address,
        city: inputProps.listing.city,
        state: inputProps.listing.state,
        zip: inputProps.listing.zip,
        beds: inputProps.listing.beds,
        baths: inputProps.listing.baths,
        halfbaths: inputProps.listing.halfbaths,
        footage: inputProps.listing.footage
      }

      // Set the images on the trimmed listing
      if (inputProps.listing?.enhancements?.data?.images && inputProps.listing.enhancements.data.images.length) {
        trimmedListing.enhancements = {
          data: {
            images: inputProps.listing.enhancements.data.images
          }
        }
      } else if (inputProps.listing.imageUrls) {
        trimmedListing.imageUrls = inputProps.listing.imageUrls;
      } else if (inputProps.listing.photos) {
        trimmedListing.photos = inputProps.listing.photos;
      }

      // Conditionally add manual listing props
      if (inputProps.listing.manual) {
        trimmedListing.manual = true;
        trimmedListing.mls_id = inputProps.listing.mls_id;
        trimmedListing.coverphoto_url = inputProps.listing.coverphoto_url;
        trimmedListing.manualType = inputProps.listing.manualType;
      }

      inputProps.listing = trimmedListing;
    }

    fetch(`${process.env.REACT_APP_KVCORE_API_URI}/v2/internal/clm/video/render`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${jwt}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        inputProps,
        account: this.state.account,
        jwt,
        voiceoverFile: this.state.voiceoverS3Url,
        isUpdate,
        assetId,
        templateId
      })
    }).catch((error) => {
      console.log('response error:', error);
    }).finally(() => {
      this.setState({rendering: false});
    });


    this.setShowExportModal(true);
  }

  setSelectedFile = (file) => {
    if (file) {
      this.setState({selectedVoiceoverFile: file, uploadingVoiceover: true})

      // Set up a FormData to pass the file to the server
      const formData = new FormData();
      formData.append('voiceover', file);

      // Upload the file to S3 to be added to video during render
      fetch(`${process.env.REACT_APP_KVCORE_API_URI}/v2/internal/clm/video/voiceover`, {
        method: 'POST',
        headers: {'Authorization': `Bearer ${jwt}`},
        body: formData
      }).then(response => response.json()).then((data) => {
        // Save temp file location to be passed to `render` endpoint
        this.setState({voiceoverS3Url: data.url});
      }).catch((error) => {
        console.log('error:', error);
      }).finally(() => {
        this.setState({uploadingVoiceover: false});
      });
    } else {
      // Input was cleared, so reset voiceoverS3Url to null
      this.setState({voiceoverS3Url: null, selectedVoiceoverFile: null, uploadingVoiceover: false});
    }
  }

  getVideoLength = () => {
    if(!this.state.selectedImages) return 1;
    let count = 0;
    this.state.selectedImages.forEach(img => {
      if(img.show) {
        count++;
      }
    })
    return count * 100;
  }

  getPlayer = () => {
    if (this.state.listingData && this.state.imagesReady) {
      return (<Player
        component={MyComp}
        inputProps={this.getInputProps()}
        durationInFrames={this.getVideoLength()}
        compositionWidth={1920}
        compositionHeight={1080}
        fps={30}
        style={{
          width: "100%",
          boxShadow: "2px 4px 12px rgba(0, 0, 0, 0.25)",
          maxWidth: "1080px"
        }}
        clickToPlay
        controls
        ref={this.player}
      />);
    } else if (this.state.processingImages) {
      return (<div className="imageProgressContainer">
        <h1>{i18n.t(this.state.locale, 'processingImages')}</h1>
        <ProgressBar
          bgColor={'#F2F2F2'}
          progressColor={'#6AB4E4'}
          percent={this.state.currentImage / this.state.totalImages}
        />
      </div>)
    } else {
      return (<GettingStartedPanel locale={this.state.locale}/>);
    }
  }

  getInputProps() {
    return {
      disclaimerActive: this.state.disclaimerActive,
      listing: this.state.listingData,
      user: this.state.selectedAgent,
      coAgent: this.state.selectedCoAgent,
      team: this.state.selectedTeam,
      brokerage: this.state.selectedBrokerage,
      brand: this.state.selectedBrand,
      scope: this.state.selectedScope,
      song: this.state.selectedSong,
      musicEnabled: this.state.musicEnabled,
      images: this.state.selectedImages,
      settings: this.state.settings,
      voiceoverEnabled: this.state.voiceoverEnabled,
      voiceoverFile: this.state.selectedVoiceoverFile,
      preloadedVoiceoverFile: this.state.preloadedVoiceoverFile,
      office: this.state.user.office,
      locale: this.state.locale
    }
  }

  render() {
    return this.state.canRenderApp && !this.state.loadingListing && (this.state.mode === 'create' || (isUpdate && assetId && this.state.preloadedDataReady))
      ? (
      <div className="App">
          <div className="App-top">
            <div className="App-header">
              <div className="App-header-left">
                {i18n.t(this.state.locale, 'listingVideo')}
              </div>
            </div>
          </div>
        <div className="App-main">
          <div className="App-card-main">
           {this.getPlayer()}
           {this.state.listingData
               ?
               <ImageSelector
                   listing={this.state.listingData}
                   ref={this.imageSelector}
                   onImagesChange={this.setImages}
                   preload={this.state.mode === 'edit' && !this.state.imagesWereReset}
                   preloadedImages={this.state.selectedImages}
                   locale={this.state.locale}
               />
               : ('')
           }
          </div>
          <div style={{ width: "25%", display: "flex", flexDirection: "column", gap: "1rem", minWidth: "395px" }}>
            <div className="App-card-right" style={{backgroundColor: "#FFF1C9"}}>
              <div style={{display: "flex", marginBottom: "20px"}}>
                  <div style={{display: "grid", marginRight: "14px"}}>
                    <svg className={"icons-color"} style={{alignSelf: "center"}} stroke="currentColor" fill="currentColor" width="17" height="20" viewBox="0 0 18 19" xmlns="http://www.w3.org/2000/svg">
                      <path d="M9 0.5C4.032 0.5 0 4.532 0 9.5C0 14.468 4.032 18.5 9 18.5C13.968 18.5 18 14.468 18 9.5C18 4.532 13.968 0.5 9 0.5ZM9.9 14H8.1V8.6H9.9V14ZM9.9 6.8H8.1V5H9.9V6.8Z"/>
                    </svg>
                  </div>
                  <div style={{fontSize: "18px", color: "#444444"}}>
                      {i18n.t(this.state.locale, 'howItWorks')}
                  </div>
              </div>
              <ul style={{fontSize: "13px"}}>
                <li>{i18n.t(this.state.locale, 'selectListing')}</li>
                <li>{i18n.t(this.state.locale, 'chooseOptions')}</li>
                <li>{i18n.t(this.state.locale, 'generateAndTakeAction')}</li>
              </ul>
            </div>
            <div className="App-card-right">
              <ListingSelector
                onListingChange={this.setValue}
                jwt={jwt}
                preload={this.state.mode === 'edit' || (mlsId && listingId)}
                preloadedListing={this.state.listingData}
                locale={this.state.locale}
              />

              <div style={{ marginBottom: "10px", marginTop: "20px" }}>
                <span className={styles.baseSwitch}>
                <span className={styles.materialSwitch} onClick={this.setDisclaimerEnabled}>
                    <input
                        type="checkbox"
                        readOnly
                        checked={this.state.disclaimerActive}
                    />
                    <label className="label-primary">&nbsp;</label>
                  </span>
                  <label className={styles.switchLabel}><slot>{i18n.t(this.state.locale, 'disclaimer')}</slot></label>
              </span>
              </div>
            </div>
            <div className="App-card-right">
                <BrandingSelector
                  jwt={jwt}
                  listing={this.state.listingData}
                  ref={this.brandingSelector}
                  authenticatedUser={this.state.user}
                  onAgentChange={this.setAgent}
                  onCoagentChange={this.setCoAgent}
                  onTeamChange={this.setTeam}
                  onBrokerageChange={this.setBrokerage}
                  onBrandChange={this.setBrand}
                  onScopeChange={this.setScope}
                  disabledOptions={this.state.disabledBranding}
                  preload={this.state.mode === 'edit'}
                  preloadedScope={this.state.selectedScope}
                  preloadedAgent={this.state.selectedAgent}
                  preloadedCoAgent={this.state.selectedCoAgent}
                  preloadedTeam={this.state.selectedTeam}
                  preloadedBrokerage={this.state.selectedBrokerage}
                  preloadedBrand={this.state.selectedBrand}
                  locale={this.state.locale}
                  templateId={templateId}
                />
            </div>
            <div className="App-card-right">
              <MusicSelector
                  onMusicChange={this.setSong}
                  onMusicEnabledChange={this.setMusicEnabled}
                  onVoiceoverEnabledChange={this.setVoiceoverEnabled}
                  onClearPreloadedVoiceover={this.clearPreloadedVoiceover}
                  onFileSelected={this.setSelectedFile}
                  preload={this.state.mode === 'edit'}
                  preloadEnableMusic={this.state.musicEnabled}
                  preloadedMusicValue={this.state.selectedSong}
                  preloadEnableVoiceover={this.state.voiceoverEnabled}
                  preloadedVoiceoverFile={this.state.preloadedVoiceoverFile}
                  locale={this.state.locale}
              />
            </div>
            <div className="App-card-right">
              <div style={{display: "flex", marginBottom: "20px"}}>
                <div style={{display: "grid", marginRight: "14px"}}>
                  <svg className={"icons-color"} style={{alignSelf: "center"}} stroke="currentColor" fill="currentColor" width="20" height="18" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24"><path d="M4,6H20V16H4M20,18A2,2 0 0,0 22,16V6C22,4.89 21.1,4 20,4H4C2.89,4 2,4.89 2,6V16A2,2 0 0,0 4,18H0V20H24V18H20Z" /></svg>
                </div>
                <div style={{fontSize: "18px", color: "#444444"}}>
                  {i18n.t(this.state.locale, 'actions')}
                </div>
              </div>
              <div className="Action-buttons-container">
                <button
                  className="Action-button"
                  onClick={this.exportVideo}
                  disabled={!(this.state.user && this.state.account && this.state.listingData && !this.state.rendering && !this.state.uploadingVoiceover)}
                >
                  <img style={{alignSelf: "center"}} alt={i18n.t(this.state.locale, 'generateVideoIconAlt')} src="../generate-video.svg" height="25" />
                  <span>{i18n.t(this.state.locale, 'generateVideo')}</span>
                </button>
                <button
                    className="Action-button"
                    onClick={this.confirmResetImages}
                    disabled={!this.state.listingData || this.state.rendering}
                >
                  <img style={{alignSelf: "center"}} alt={i18n.t(this.state.locale, 'resetImagesIconAlt')} src="../reset-images.svg" height="25" />
                  <span>{i18n.t(this.state.locale, 'resetPhotos')}</span>
                </button>
              </div>
            </div>
          </div>
        </div>
        <ExportVideoModal
          show={this.state.showExportModal}
          onClose={() => this.setShowExportModal(false)}
          locale={this.state.locale}
        />
        { this.state.showResetPhotosConfirmation &&
          <BaseConfirm
            title={i18n.t(this.state.locale, 'areYouSure')}
            subtitle={i18n.t(this.state.locale, 'confirmResetPhotosMsg')}
            confirmButtonText={i18n.t(this.state.locale, 'confirmButtonText')}
            cancelButtonText={i18n.t(this.state.locale, 'cancelButtonText')}
            onConfirmed={ this.confirmedResetImages }
            onCancelled={ this.cancelledResetImages }
        />}
      </div>
    )
    : (
      <div className="AppLoadingOverlay">
        <img src="../loading.svg" alt="loading icon" />
      </div>
    );
  }
}

export default App;
